import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Landmark, ArrowRight, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * CommonsPrompt — quiet, inline "Make this group last" card.
 *
 * Behaviour (per directive):
 *  - Inline, not sticky, not a modal.
 *  - Only shown to the creator/steward of a non-Commons group.
 *  - Only shown after real activity: ≥ 2 completed activities OR ≥ 3 created.
 *  - Dismissal is permanent (per-user per-group). Not repeated aggressively.
 *
 * The backend decides `show`. The component only renders if `show` is true.
 * All copy stays aligned with the language directive: "Make this group last",
 * no upgrade / unlock / premium / earn / rewards language.
 */
export default function CommonsPrompt({ groupId }) {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/api/groups/${groupId}/commons-prompt`,
          { withCredentials: true },
        );
        if (!cancelled) setShow(Boolean(res.data?.show));
      } catch {
        // Silent — prompt just won't appear. It's optional UX, not critical.
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    setShow(false); // optimistic
    try {
      await axios.post(
        `${API}/api/groups/${groupId}/commons-prompt/dismiss`,
        {},
        { withCredentials: true },
      );
    } catch {
      // Even if the server call fails, keep it hidden for this session.
    } finally {
      setDismissing(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="rounded-2xl p-4 flex items-start gap-3"
      style={{
        background: 'linear-gradient(135deg, #8B6A4F14 0%, #8B6A4F08 100%)',
        border: '1px dashed #8B6A4F55',
      }}
      data-testid="commons-prompt"
    >
      <div
        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center"
        style={{ backgroundColor: '#8B6A4F22' }}
      >
        <Landmark className="w-5 h-5" style={{ color: '#6D5238' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="text-sm font-semibold"
          style={{ color: 'var(--fy-text-primary)' }}
          data-testid="commons-prompt-title"
        >
          Make this group last
        </p>
        <p
          className="text-xs mt-1"
          style={{ color: 'var(--fy-text-secondary)' }}
        >
          This group keeps showing up. Commons keeps it going over time.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/commons/start?group_id=${encodeURIComponent(groupId)}`)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-transform active:scale-95"
          style={{ backgroundColor: '#8B6A4F', color: '#FFFFFF' }}
          data-testid="commons-prompt-cta"
        >
          Learn more <ArrowRight className="w-3 h-3" />
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 -mr-1 -mt-1 rounded-full hover:opacity-70 flex-shrink-0"
        aria-label="Dismiss"
        data-testid="commons-prompt-dismiss"
      >
        <X className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
      </button>
    </div>
  );
}
